import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';
import { getSingleVideo, updateVideo, uploadVideo } from '.';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  AdminPageLayout,
  AdminSectionCard,
  VideoSourceSection,
  getVideoSourceFromUrl,
  AdminAction,
  AdminFormFooter,
} from '../../components/admin';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { isURLValid } from '../../utils/rgexValidation';
import { extractYouTubeVideoId, getYouTubeThumbnailUrl } from '../../utils/youtube';
import CategorySubcategoryFields from '../../components/common/CategorySubcategoryFields';
import { useCategorySubcategory } from '../../hooks/useCategorySubcategory';
import { languages } from '../video';

const languageFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.75rem',
  },
};

function resolveLanguageName(value) {
  if (!value) return '';
  const normalized = String(value).trim().toLowerCase();
  const match =
    languages.find((item) => item.name.toLowerCase() === normalized) ||
    languages.find((item) => item.lang.toLowerCase() === normalized) ||
    languages.find((item) => item.langCode.toLowerCase() === normalized);
  return match?.name || '';
}

const inputClassName =
  'block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4';

function getApiErrorMessage(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.data?.message ||
    error.message ||
    fallback
  );
}

function isLocalUploadPath(url) {
  if (!url) return false;
  const normalized = String(url).trim();
  return normalized.startsWith('uploads/') || normalized.startsWith('/uploads/');
}

function formatPublishedAt(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function EditVideo() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[2];
  const isCreateMode = pathSegment === 'upload';
  const isEditMode = !isCreateMode && !isNaN(pathSegment);

  const [ID, setId] = useState(null);
  const [data, setData] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [title, setTitle] = useState('');
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [expertName, setExpertName] = useState('');
  const [language, setLanguage] = useState('');
  const [description, setdescription] = useState('');
  const [URL, setURL] = useState('');
  const [videoSource, setVideoSource] = useState('url');
  const [videoFile, setVideoFile] = useState(null);
  const [existingVideoPath, setExistingVideoPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [image, setImage] = useState('/placeholder-image.jpg');
  const [categoryInitialized, setCategoryInitialized] = useState(false);

  const {
    categories,
    category,
    subCategory,
    subCategories,
    loadingSubCategories,
    subCategoryFieldKey,
    categoryName,
    subCategoryName,
    isSubCategoryValid,
    handleCategoryChange,
    handleSubCategoryChange,
    initializeFromNames,
  } = useCategorySubcategory();

  useEffect(() => {
    if (isEditMode) {
      setId(pathSegment);
      setThumbnailFile('existing');
    }
  }, [isEditMode, pathSegment]);

  const fetchData = async () => {
    try {
      const response = await getSingleVideo(ID);
      const responseData = response.data;
      setData(responseData?.data || null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load video'), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
        theme: 'light',
      });
    }
  };

  useEffect(() => {
    if (isEditMode && ID !== null) {
      fetchData();
    }
  }, [ID, isEditMode]);

  useEffect(() => {
    if (data !== null) {
      const source = getVideoSourceFromUrl(data.url);
      setExistingImagePath(data.highImageUrl);
      setImage(resolveMediaUrl(data.highImageUrl));
      setTitle(data.title || '');
      setdescription(data.description || '');
      setLanguage(resolveLanguageName(data.language));
      setExpertName(data.expertName || '');
      setKeyword(data.keyword || '');
      setVideoSource(source);

      if (source === 'upload') {
        setExistingVideoPath(data.url);
        setURL('');
      } else {
        setURL(data.url || '');
        setExistingVideoPath(null);
      }
    }
  }, [data]);

  useEffect(() => {
    if (data && !categoryInitialized) {
      initializeFromNames(data.category, data.subCategory).finally(() => {
        setCategoryInitialized(true);
      });
    }
  }, [data, categoryInitialized, initializeFromNames]);

  const handleVideoSourceChange = (source) => {
    setVideoSource(source);
    setVideoFile(null);
    if (source === 'url') {
      if (isLocalUploadPath(existingVideoPath)) {
        setURL('');
      }
    } else if (isLocalUploadPath(data?.url)) {
      setExistingVideoPath(data.url);
    } else {
      setExistingVideoPath(null);
    }
  };

  const handleVideoFileChange = (file, errorMessage) => {
    if (errorMessage) {
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }
    setVideoFile(file);
  };

  const applyYouTubeThumbnail = (videoUrl) => {
    const videoId = extractYouTubeVideoId(videoUrl);
    if (!videoId) {
      // Clear auto YouTube thumbnail when URL is no longer a valid YouTube link
      setThumbnailFile((prev) => {
        if (prev === 'youtube') {
          setExistingImagePath(null);
          setImage('/placeholder-image.jpg');
          return null;
        }
        return prev;
      });
      return;
    }

    const thumbUrl = getYouTubeThumbnailUrl(videoId);
    setImage(thumbUrl);
    setExistingImagePath(thumbUrl);
    setThumbnailFile('youtube');
  };

  const handleVideoUrlChange = (value) => {
    setURL(value);
    applyYouTubeThumbnail(value);
  };

  const hasThumbnail =
    thumbnailFile instanceof File ||
    thumbnailFile === 'youtube' ||
    thumbnailFile === 'existing' ||
    Boolean(existingImagePath && existingImagePath !== '/placeholder-image.jpg');

  const handleSubmit = async () => {
    if (videoSource === 'url' && URL && !isURLValid(URL)) {
      toast.error('Invalid URL format! URL must start with http:// or https://', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }

    if (
      !hasThumbnail ||
      !title ||
      !categoryName ||
      !subCategoryName ||
      !language ||
      !description ||
      !keyword ||
      !expertName ||
      (videoSource === 'url' && !URL) ||
      (videoSource === 'upload' && !videoFile && !isLocalUploadPath(existingVideoPath))
    ) {
      toast.error('Required fields are missing!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
        theme: 'light',
      });
      return;
    }

    if (!isSubCategoryValid) {
      toast.error('Please select a valid subcategory for the chosen category.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
        theme: 'light',
      });
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);
      const formData = new FormData();
      formData.append('title', title);
      formData.append('category', categoryName);
      formData.append('subCategory', subCategoryName);
      formData.append('language', language);
      formData.append('description', description);
      formData.append('keyword', keyword);
      formData.append('expertName', expertName);
      formData.append('videoSource', videoSource);

      if (isCreateMode) {
        formData.append('publishedAt', formatPublishedAt());

        if (thumbnailFile instanceof File) {
          formData.append('image', thumbnailFile);
        } else if (thumbnailFile === 'youtube' && existingImagePath) {
          // Backend downloads the YouTube CDN thumbnail and stores it under uploads/
          formData.append('thumbnailUrl', existingImagePath);
        } else {
          toast.error('Please upload a thumbnail image.', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            theme: 'light',
          });
          setLoading(false);
          setUploadProgress(null);
          return;
        }

        if (videoSource === 'upload') {
          formData.append('videoFile', videoFile);
        } else {
          formData.append('videoUrl', URL);
        }

        const response = await uploadVideo(formData, (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        });

        if (response) {
          navigate('/videoUpload', { state: { results: { title } } });
        }
      } else {
        if (thumbnailFile instanceof File) {
          formData.append('image', thumbnailFile);
        } else if (thumbnailFile === 'youtube' && existingImagePath) {
          formData.append('thumbnailUrl', existingImagePath);
        } else if (existingImagePath) {
          formData.append('image', existingImagePath);
        }

        if (videoSource === 'upload') {
          if (videoFile) {
            formData.append('videoFile', videoFile);
          } else if (existingVideoPath) {
            formData.append('existingVideoUrl', existingVideoPath);
          }
        } else {
          formData.append('videoUrl', URL);
        }

        const response = await updateVideo(ID, formData, (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        });

        if (response.status === 200) {
          navigate('/videoUpload', { state: { update: true } });
        } else if (response.status === 202) {
          toast.error(response.data.message, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            theme: 'light',
          });
        }
      }
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          isCreateMode ? 'Error uploading video!' : 'Error updating video!'
        ),
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: true,
          theme: 'light',
        }
      );
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const changeHandler = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    const allowedFormats = ['image/png', 'image/webp', 'image/jpeg'];
    if (!allowedFormats.includes(selectedFile.type)) {
      event.target.value = '';
      toast.error('Image type should be image/png, image/webp, image/jpeg', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }
    setThumbnailFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const pageTitle = isCreateMode ? 'Upload Video' : 'Edit Video';
  const pageDescription = isCreateMode
    ? 'Add a new video with thumbnail, metadata, and category mapping.'
    : 'Update video details, metadata, thumbnail, and video source.';
  const sectionDescription = isCreateMode
    ? 'Enter the title, metadata, thumbnail, and video source for this video.'
    : 'Update the title, metadata, thumbnail, and video source for this video.';
  const submitLabel = isCreateMode ? 'Upload Video' : 'Update Video';

  return (
    <AdminPageLayout
      eyebrow="Content Library"
      title={pageTitle}
      description={pageDescription}
      actions={
        <AdminAction
          variant="outline"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate('/videoUpload')}
        >
          Back to listing
        </AdminAction>
      }
    >
      <AdminSectionCard
        title="Video details"
        description={sectionDescription}
      >
        {isEditMode && data?.creator?.fullName && (
          <p className="mb-4 text-sm text-stone-600">
            <span className="font-medium text-stone-700">Created By:</span>{' '}
            {data.creator.fullName}
          </p>
        )}

        <div className="admin-form-body overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  placeholder="Enter video title"
                  className={inputClassName}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Description *
                </label>
                <textarea
                  className={`${inputClassName} min-h-[100px] resize-y`}
                  placeholder="Enter video description"
                  name="description"
                  value={description || ''}
                  rows="4"
                  onChange={(e) => setdescription(e.target.value)}
                />
              </div>

              <CategorySubcategoryFields
                layout="form"
                required
                categories={categories}
                category={category}
                subCategories={subCategories}
                subCategory={subCategory}
                loadingSubCategories={loadingSubCategories}
                subCategoryFieldKey={subCategoryFieldKey}
                onCategoryChange={handleCategoryChange}
                onSubCategoryChange={handleSubCategoryChange}
              />

              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2" htmlFor="language-field">
                  Language *
                </label>
                <Autocomplete
                  disablePortal
                  id="language-field"
                  options={languages}
                  value={languages.find((item) => item.name === language) || null}
                  onChange={(_event, newValue) => setLanguage(newValue?.name || '')}
                  isOptionEqualToValue={(option, value) => option?.name === value?.name}
                  getOptionLabel={(option) => option?.name || ''}
                  size="small"
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <span>{option.name}</span>
                      {option.lang && option.lang !== option.name ? (
                        <span className="ml-2 text-stone-500">({option.lang})</span>
                      ) : null}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select language"
                      sx={languageFieldSx}
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Keyword *
                </label>
                <input
                  type="text"
                  value={keyword}
                  placeholder="Keywords"
                  className={inputClassName}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Expert Name *
                </label>
                <input
                  type="text"
                  value={expertName}
                  placeholder="Expert name"
                  className={inputClassName}
                  onChange={(e) => setExpertName(e.target.value)}
                />
              </div>

              <VideoSourceSection
                videoSource={videoSource}
                onSourceChange={handleVideoSourceChange}
                url={URL}
                onUrlChange={handleVideoUrlChange}
                videoFile={videoFile}
                onVideoFileChange={handleVideoFileChange}
                existingUploadPath={existingVideoPath}
                uploadProgress={uploadProgress}
              />
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-3">
                Video Thumbnail
              </label>
              <div
                className="min-h-[200px] border-2 border-dashed rounded-xl bg-stone-50 flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-stone-100 hover:border-stone-400"
                onClick={() => document.getElementById('video-thumbnail-input').click()}
              >
                <input
                  id="video-thumbnail-input"
                  type="file"
                  accept="image/png, image/webp, image/jpeg"
                  onChange={changeHandler}
                  className="hidden"
                />
                {image ? (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={image}
                      alt="Thumbnail preview"
                      className="max-h-[180px] object-contain rounded-lg"
                      onError={(e) => {
                        if (e.currentTarget.src.includes('/maxresdefault.jpg')) {
                          const fallback = e.currentTarget.src.replace(
                            '/maxresdefault.jpg',
                            '/hqdefault.jpg'
                          );
                          setImage(fallback);
                          setExistingImagePath(fallback);
                          return;
                        }
                        setImage('');
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center p-6">
                    <svg className="mx-auto h-12 w-12 text-stone-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-stone-700 font-medium">Click or drag file to this area to upload</p>
                    <p className="text-stone-400 text-sm mt-1">PNG, WEBP, JPEG (max 5MB)</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <AdminFormFooter>
          <AdminAction
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            {loading && uploadProgress != null && uploadProgress > 0 && uploadProgress < 100
              ? `Uploading ${uploadProgress}%...`
              : submitLabel}
          </AdminAction>
        </AdminFormFooter>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
